import type {ReactElement} from 'react';
import React from 'react';
import type {VideoItem} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import {ContextualMenuMediaControlOptions} from '@Components/contextual-menu/components/contextual-menu-media-control-options';
import type {MediaControlsItemProps} from '@Components/contextual-menu/components/media-controls-item';
import {onClickRemoveVideoBG, onOpenReplaceMedia, onOpenTrimVideoModal, onOpenVideoCropModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import type {ItemData} from '@PosterWhiteboard/page/add-item.class';
import type {ReplaceMediaControlsButtons} from '@Components/replace-media-controls';
import {ReplaceMediaControls} from '@Components/replace-media-controls';
import {RemoveVideoBackgroundNotificationTag} from '@Components/poster-editor/components/remove-video-background-notification-tag';
import {GA4EventName, GA4EventParamName, GA4EventSources, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {useGetTeamCredits} from '@Hooks/useGetTeamCredits';
import {RemoveBackgroundStatus} from '@PosterWhiteboard/items/item/item.types';
import {FreeBgrPopup} from '@Components/free-bgr-popup';
import {useAppSelector} from '@/hooks';
import {VIDEO_BGR_PROMO_MAX_DURATION_SECONDS} from '@Libraries/user.library';

interface BaseVideoMediaControlsProps {
  activeItem: VideoItem | VideoSlideItem;
  removeBackgroundStatus: RemoveBackgroundStatus;

  onReplaceVideo(itemData: ItemData): void;

  onUnMute(): void;
}

enum VideoMediaControlsOptions {
  CROP = 'crop',
  TRIM = 'trim',
  REPLACE = 'replace',
  REMOVE_BG = 'remove-bg',
  UNMUTE = 'unmute',
}

export function BaseVideoMediaControls({...props}: BaseVideoMediaControlsProps): ReactElement {
  const credits = useGetTeamCredits() ?? 0;
  const videoBGRPromo = useAppSelector((state) => state.teamCredits.videoBGRPromo);

  const onCropVideo = (): void => {
    onOpenVideoCropModal(props.activeItem);
  };

  const onTrimVideo = (): void => {
    onOpenTrimVideoModal(props.activeItem);
  };

  const onReplaceCallback = (id: ReplaceMediaControlsButtons): void => {
    onOpenReplaceMedia(id, props.activeItem, (itemData: ItemData): void => {
      props.onReplaceVideo(itemData);
    });
  };

  const onClickRemoveBG = async (): Promise<void> => {
    await onClickRemoveVideoBG(props.activeItem, credits);
    trackPosterBuilderGA4Events(GA4EventName.REMOVE_BACKGROUND_VIDEO, {[GA4EventParamName.SOURCE]: GA4EventSources.MOBILE_BOTTOM_BAR});
  };

  const getCustomPopupForBGR = (): ReactElement | undefined => {
    const duration = props.activeItem.getEndTimeForNonRemovedBgVideo() - props.activeItem.getStartTimeForNonRemovedBgVideo();
    if (videoBGRPromo?.isEligible && props.removeBackgroundStatus === RemoveBackgroundStatus.NONE && duration <= VIDEO_BGR_PROMO_MAX_DURATION_SECONDS) {
      return <FreeBgrPopup isVideo />;
    }
    return undefined;
  };

  const getItems = (): MediaControlsItemProps[] => {
    return [
      {
        id: `${VideoMediaControlsOptions.REMOVE_BG}-media-control`,
        icon: 'icon-remove-bg',
        text: window.i18next.t('pmwjs_remove_bg'),
        isLoading: props.removeBackgroundStatus === RemoveBackgroundStatus.REMOVING,
        loadingText: window.i18next.t('pmwjs_removing_background'),
        tag: (
          <RemoveVideoBackgroundNotificationTag
            videoHashedFilename={props.activeItem.hashedFilename}
            startTime={props.activeItem.getStartTimeForNonRemovedBgVideo()}
            endTime={props.activeItem.getEndTimeForNonRemovedBgVideo()}
          />
        ),
        iconClasses: 'content-ai',
        textColorClasses: 'content-ai',
        isSelected: props.removeBackgroundStatus === RemoveBackgroundStatus.REMOVED,
        onClick: (): void => {
          void onClickRemoveBG();
        },
        textPaddingClasses: 'spacing-p-r-1 spacing-p-l-1',
        tagContainerClassName: '_full-width',
        customPopup: getCustomPopupForBGR(),
      },
      {
        id: `${VideoMediaControlsOptions.CROP}-video-controls`,
        icon: 'icon-crop',
        text: window.i18next.t('pmwjs_crop'),
        onClick: onCropVideo,
      },
      {
        id: `${VideoMediaControlsOptions.TRIM}-video-controls`,
        icon: 'icon-cut',
        text: window.i18next.t('pmwjs_trim'),
        onClick: onTrimVideo,
      },
      {
        id: `${VideoMediaControlsOptions.REPLACE}-video-controls`,
        icon: 'icon-replace',
        text: window.i18next.t('pmwjs_replace'),
        dropdownContent: (
          <ReplaceMediaControls
            onClick={(id: ReplaceMediaControlsButtons): void => {
              onReplaceCallback(id);
            }}
          />
        ),
      },
    ];
  };

  return <ContextualMenuMediaControlOptions id="video-media-controls" items={getItems()} />;
}
